import { create } from "zustand";
import { getStoredToken, setStoredToken } from "../api/client";
import {
  getProfile,
  refreshToken,
  login as apiLogin,
  logout as apiLogout,
  verifyOtp,
  normalizeAuthUser,
} from "../api/authEtablissement";
import type { EstablishmentUser } from "../api/authEtablissement";

// Clefs “legacy” encore présentes possibles après une ancienne version (token localStorage + persist zustand).
const LEGACY_TOKEN_KEY = "agbaza.etablissement.token";
const LEGACY_PERSIST_KEY = "agbaza.auth.establishment";

// Empêche `initAuth()` de “couper” `isLoading` pendant que l'utilisateur valide son OTP.
let otpLoginInProgress = false;

export type Establishment = {
  id?: string;
  name: string;
  email?: string;
  agre?: string;
  type?: string;
  commune?: string;
};

export type SignInCredentials = { email: string; password: string };

function resolveEstablishmentId(
  user: EstablishmentUser,
  previousEstablishment?: Establishment | null
): string | undefined {
  const businessId = typeof user.etablissementId === "string" ? user.etablissementId.trim() : "";
  if (businessId) return businessId;
  const previousId =
    typeof previousEstablishment?.id === "string" ? previousEstablishment.id.trim() : "";
  if (previousId) return previousId;
  const fallbackId = typeof user.id === "string" ? user.id.trim() : "";
  return fallbackId || undefined;
}

function userToEstablishment(
  user: EstablishmentUser | null,
  previousEstablishment?: Establishment | null
): Establishment | null {
  if (!user) return null;
  const u = normalizeAuthUser(user) ?? user;
  const nameRaw = (u.name ?? u.nom ?? "").trim();
  const emailRaw = typeof u.email === "string" ? u.email.trim() : "";
  const prevName = (previousEstablishment?.name ?? "").trim();
  const resolvedId = resolveEstablishmentId(u, previousEstablishment);
  const hasDisplay = !!(nameRaw || emailRaw || prevName);
  if (!resolvedId && !hasDisplay) return null;
  const name = nameRaw || emailRaw || prevName || "Établissement";
  return {
    id: resolvedId,
    name,
    email: emailRaw || previousEstablishment?.email,
    agre: u.agrement ?? u.agre ?? previousEstablishment?.agre,
    type: u.type ?? u.type_utilisateur ?? previousEstablishment?.type,
    commune: u.commune ?? previousEstablishment?.commune,
  };
}

export type SignInResult =
  | { ok: true; requiresOtp?: boolean }
  | { ok: false; message: string };

type AuthState = {
  establishment: Establishment | null;
  isLoading: boolean;
  setEstablishment: (est: Establishment | null) => void;
  signIn: (credentials: SignInCredentials) => Promise<SignInResult>;
  confirmLoginOtp: (email: string, code: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  clearAuth: () => void;
  initAuth: () => Promise<void>;
};

export const useAuthEtablissementStore = create<AuthState>()((set, get) => ({
  establishment: null,
  isLoading: true,

  setEstablishment: (est) => set({ establishment: est }),

  signIn: async (credentials) => {
    const result = await apiLogin(credentials.email, credentials.password);
    if (!result.ok) return { ok: false, message: result.message };
    if ("requiresOtp" in result.data && result.data.requiresOtp) {
      return { ok: true, requiresOtp: true };
    }
    // Flux actuel côté backend : le token est stocké après verify-otp.
    const user = "user" in result.data ? result.data.user : null;
    if (user) {
      const est = userToEstablishment(user, get().establishment);
      if (est) set({ establishment: est });
    }
    return { ok: true };
  },

  confirmLoginOtp: async (email, code) => {
    otpLoginInProgress = true;
    set({ isLoading: true });
    try {
      const result = await verifyOtp(email, code);
      if (!result.ok) return { ok: false, message: result.message };

      if (result.data?.user) {
        const est = userToEstablishment(result.data.user, get().establishment);
        if (est) set({ establishment: est });
      }
      return { ok: true };
    } finally {
      otpLoginInProgress = false;
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    await apiLogout();
    set({ establishment: null });
  },

  clearAuth: () => {
    setStoredToken(null);
    set({ establishment: null, isLoading: false });
    try {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      localStorage.removeItem(LEGACY_PERSIST_KEY);
    } catch {
      /* ignore */
    }
  },

  initAuth: async () => {
    // S'assurer que le guard a toujours un `isLoading=true` pendant la résolution session.
    set({ isLoading: true });

    try {
      // Si l'utilisateur se connecte pendant que initAuth est en cours (OTP),
      // on ne doit pas écraser l'état fraîchement hydraté.
      const tokenAtStart = getStoredToken();

      // Nettoyage des anciennes clés persistées (ancienne version front).
      try {
        localStorage.removeItem(LEGACY_TOKEN_KEY);
        localStorage.removeItem(LEGACY_PERSIST_KEY);
      } catch {
        /* ignore */
      }

      const refresh = await refreshToken();
      if (!refresh.ok) {
        // Ne pas déconnecter si l'utilisateur s'est connecté entre-temps.
        if (!getStoredToken() && !tokenAtStart) {
          setStoredToken(null);
          set({ establishment: null });
        }
        return;
      }

      const profileRes = await getProfile({ silent401: true });
      if (profileRes.ok) {
        set({ establishment: userToEstablishment(profileRes.data, get().establishment) });
      } else {
        // Ne pas écraser un établissement défini pendant un initAuth concurrent.
        if (!getStoredToken() && !tokenAtStart) {
          set({ establishment: null });
        }
      }
    } catch {
      // Erreur réseau : on laisse le guard décider via isLoading.
    } finally {
      // Si l'utilisateur est en train de valider son OTP, on ne coupe pas `isLoading`
      // (il sera géré par `confirmLoginOtp`).
      if (!otpLoginInProgress) set({ isLoading: false });
    }
  },
}));

export function useAuth() {
  const establishment = useAuthEtablissementStore((s) => s.establishment);
  const isLoading = useAuthEtablissementStore((s) => s.isLoading);
  const setEstablishment = useAuthEtablissementStore((s) => s.setEstablishment);
  const signIn = useAuthEtablissementStore((s) => s.signIn);
  const confirmLoginOtp = useAuthEtablissementStore((s) => s.confirmLoginOtp);
  const signOut = useAuthEtablissementStore((s) => s.signOut);

  return {
    establishment,
    setEstablishment,
    isAuthenticated: !isLoading && !!establishment,
    isLoading,
    signIn,
    confirmLoginOtp,
    signOut,
  };
}
